import React, { createContext, useState, useContext, useEffect } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { withSize } from 'react-sizeme';
import classnames from 'classnames';

import ProgressBar from '../components/ProgressBar';
import ProgressIndicator from '../components/ProgressIndicator';
import ProgressIndicatorModal from '../components/ProgressIndicatorModal';
import Sizes from '../definitions/Sizes';
import WizardSizes from '../definitions/WizardSizes';
import Colours from '../definitions/Colours';
import scrollToFirstInvalidField from '../functions/scrollToFirstInvalidField';
import { FORM_KEY } from './Form';


const LAYOUT_SIZES = {
    GAP_SMALL: Sizes.SPACING.ONE,
    GAP_LARGE: Sizes.SPACING.TWO,

    SIDEBAR_SMALL: 'minmax(10.5rem, 16rem)',
    SIDEBAR_LARGE: 'minmax(10.5rem, 18.5rem)',

    MAIN_SMALL: 'minmax(auto, 42.5rem)',
    MAIN_LARGE: 'minmax(32rem, 50rem)',

    LEFT_SIDEBAR_TOP: WizardSizes.AUTO_FOCUS_QUESTION_TOP,
    LEFT_SIDEBAR_MARGIN_TOP: WizardSizes.PAGE_INTRO_HEIGHT,
    PROGRESS_INDICATOR_MODAL_MIN_WIDTH: 300,
};

function WizardLayout({ children, ...otherProps }) {
    const contextState = useWizardLayoutState();

    return (
        <WizardLayoutContext.Provider value={contextState}>
            <WizardLayout.Container
                heightAboveProgressIndicator={
                    contextState?.topLeftContainerHeight ?? 0
                }
                {...otherProps}
            >
                {children}
            </WizardLayout.Container>
        </WizardLayoutContext.Provider>
    );
}

WizardLayout.propTypes = {
    children: PropTypes.node.isRequired,
};

/* Progress Bar - This component only renders a ProgressBar. */
WizardLayout.ProgressBar = styled(ProgressBar)`
    ${props =>
        props.isVisible === false
            ? `
        display: none;
        visibility: hidden;
    `
            : ''}

    grid-column-start: 1;
    grid-column-end: 6;

    grid-row-start: 1;
    grid-row-end: 2;
`;

// eslint-disable-next-line react/no-multi-comp, react/prop-types
WizardLayout.TopRowContainer = ({ children, className, ...otherProps }) => {
    return (
        <div
            className={classnames('wizard-top-row-container', className)}
            {...otherProps}
        >
            {children}
        </div>
    );
};

/* Top Left Container - This component takes children and renders above the Progress Indicator
    on Glamazon only. On smaller screens, this component takes up the full width below the
    Progress Indicator. */
WizardLayout.TopLeftContainer = withSize({
    monitorHeight: true,
    noPlaceholder: true,
})(({ children, size, ...otherProps }) => {
    const context = useWizardLayoutContext();

    useEffect(() => {
        size.height && context?.setTopLeftContainerHeight(size.height);
    }, [ size?.height, context?.setTopLeftContainerHeight ]);

    return (
        <StyledTopLeftContainer {...otherProps}>
            {children}
        </StyledTopLeftContainer>
    );
});

const StyledTopLeftContainer = styled.div`
    position: sticky;
    top: ${LAYOUT_SIZES.LEFT_SIDEBAR_TOP};

    display: flex;

    max-width: none;
    margin: ${LAYOUT_SIZES.LEFT_SIDEBAR_MARGIN_TOP} ${Sizes.SPACING.TWO} 0;

    grid-column-start: 2;
    grid-column-end: auto;

    grid-row-start: 3;
    grid-row-end: auto;
`;

/* Progress Indicator - this component renders a ProgressIndicator or ProgressIndicatorModal */
WizardLayout.ProgressIndicator = withSize({
    noPlaceholder: true,
})(
    ({
        /* Layout props */
        size,
        className,
        isVisible,

        /* ProgressIndicator props */
        steps,
        onBreadcrumbClick,
        shouldValidateOnClick = true,
        openModalButtonMessage,
        helpMessage,

        topRightModalElement,
        children,

        ...otherProps
    }) => {
        // In general, we cannot invoke the breadcrumb click if the Form is invalid, but we allow
        // an exception if shouldValidateOnClick is false
        function handleBreadcrumbClick(breadcrumb) {
            if (!onBreadcrumbClick) {
                return;
            }

            // Form[FORM_KEY] is used here to get access to our Form state.
            //  There is an assumption here that only one Form will
            //  be rendered within the WizardLayout at a time though.
            const activeForm = document?.querySelector('form')?.[FORM_KEY];

            // stop here if there is an invalid form on the page, except…
            // we ignore that if shouldValidateOnClick is false
            if (shouldValidateOnClick && activeForm && !activeForm.isValid) {
                activeForm.touchVisibleFields();
                scrollToFirstInvalidField();
                return;
            }

            onBreadcrumbClick(breadcrumb, activeForm?.values);
        }

        return (
            <Choose>
                <When
                    condition={
                        size?.width >= LAYOUT_SIZES.PROGRESS_INDICATOR_MODAL_MIN_WIDTH
                    }
                >
                    <ProgressIndicatorModal
                        className={classnames(
                            'wizard-progress-indicator',
                            className,
                            { 'is-hidden': isVisible === false },
                        )}
                        steps={steps}
                        onBreadcrumbClick={
                            onBreadcrumbClick
                                ? handleBreadcrumbClick
                                : undefined
                        }
                        openModalButtonMessage={openModalButtonMessage}
                        helpMessage={helpMessage}
                        topRightElement={topRightModalElement}
                        {...otherProps}
                    >
                        {children}
                    </ProgressIndicatorModal>
                </When>
                <Otherwise>
                    <ProgressIndicator
                        className={classnames(
                            'wizard-progress-indicator',
                            className,
                            { 'is-hidden': isVisible === false },
                        )}
                        steps={steps}
                        onBreadcrumbClick={
                            onBreadcrumbClick
                                ? handleBreadcrumbClick
                                : undefined
                        }
                        helpMessage={helpMessage}
                        {...otherProps}
                    >
                        {children}
                    </ProgressIndicator>
                </Otherwise>
            </Choose>
        );
    },
);

/* Main - This component takes children and should contain either a WizardPageTemplate
    or any Questions or Forms necessary for the wizard content. */
WizardLayout.Main = styled.div`
    max-width: none;

    grid-column: 3;
    grid-column-end: auto;

    grid-row: 3;
    grid-row-end: 6;
`;

/* Right Sidebar - This component take children and is placed on
    the right side of the main content only on a wide screen,
    on a narrow screen this component has no display. */
WizardLayout.Sidebar = styled.div`
    max-width: none;

    grid-column-start: 4;
    grid-column-end: auto;

    grid-row-start: 3;
    grid-row-end: 6;
`;

const ContainerStyles = css`
    display: grid;

    grid-template-columns: 1fr ${LAYOUT_SIZES.SIDEBAR_LARGE} ${LAYOUT_SIZES.MAIN_LARGE} ${LAYOUT_SIZES.SIDEBAR_LARGE} 1fr;
    grid-column-gap: ${LAYOUT_SIZES.GAP_LARGE};

    /* The 5 rows are necessary since the ProgressIndicator section
        requires its own row to be sticky */
    grid-template-rows: auto minmax(0, auto) minmax(0, auto) auto auto;

    padding-bottom: ${Sizes.SPACING.THREE};

    background-color: ${Colours.COCONUT_DARK};

    /* The following elements within the layout are using class
        names since this is a new pattern we'll be moving towards. */
    > .wizard-progress-indicator {
        &.is-hidden {
            display: none;
            visibility: hidden;
        }

        position: sticky;
        top: ${LAYOUT_SIZES.LEFT_SIDEBAR_TOP};

        max-width: none;
        margin: ${LAYOUT_SIZES.LEFT_SIDEBAR_MARGIN_TOP} ${Sizes.SPACING.TWO} 0 0;

        grid-column-start: 2;
        grid-column-end: auto;

        grid-row-start: 4;
        grid-row-end: auto;

        ${props =>
        props.heightAboveProgressIndicator > 0
            ? `
            top: calc(${props.heightAboveProgressIndicator}px + ${LAYOUT_SIZES.LEFT_SIDEBAR_TOP});
            margin-top: 0;
            padding-top: ${Sizes.SPACING.TWO};
        `
            : ''}
    }

    > .wizard-top-row-container {
        grid-column-start: 1;
        grid-column-end: 6;

        grid-row-start: 2;
        grid-row-end: auto;
    }

    @media (max-width: 66rem) {
        grid-template-columns: 1fr ${LAYOUT_SIZES.SIDEBAR_SMALL} ${LAYOUT_SIZES.MAIN_LARGE} 1fr;

        ${WizardLayout.Sidebar} {
            display: none;
        }

        ${WizardLayout.ProgressBar} {
            grid-column-end: 5;
        }
    }

    @media (max-width: 56rem) {
        grid-template-columns: 1fr ${LAYOUT_SIZES.MAIN_SMALL} 1fr;
        grid-column-gap: ${LAYOUT_SIZES.GAP_SMALL};

        > .wizard-top-row-container {
            display: none;
        }

        ${WizardLayout.Sidebar} {
            display: none;
        }

        ${StyledTopLeftContainer} {
            position: static;

            justify-content: center;

            margin: ${Sizes.SPACING.ONE};

            grid-column-start: 1;
            grid-column-end: 4;

            grid-row-start: 3;
        }

        > .wizard-progress-indicator {
            position: static;

            margin: 0;
            padding: 0;

            grid-column-start: 1;
            grid-column-end: 4;

            grid-row-start: 2;
        }

        ${WizardLayout.Main} {
            grid-column-start: 2;

            grid-row-start: 4;
        }

        ${WizardLayout.ProgressBar} {
            grid-column-end: 4;
        }
    }
`;

WizardLayout.Container = styled.div`
    ${ContainerStyles};
`;

/* Context */
const WizardLayoutContext = createContext();

function useWizardLayoutState() {
    const [ topLeftContainerHeight, setTopLeftContainerHeight ] = useState(0);

    return {
        topLeftContainerHeight,
        setTopLeftContainerHeight,
    };
}

function useWizardLayoutContext() {
    return useContext(WizardLayoutContext);
}

export default WizardLayout;
